/**
 * Which system roles work a form, for forms whose owner is narrower than "every role with
 * `document.*`" (user, 2026-09-30, docs/designs/processing-integrity-fixes.md): Processing and
 * Packing are strictly separate modules, and Lot Blending is its own grant.
 *
 * For a form listed here, a listed role gets its own `document.*` rights on the form, and every other
 * role that can see documents gets view only. Forms not listed keep the older rule: every role with
 * `document.*` gets those rights on every form. The seed and the live-database grant script both read
 * `seededFormGrants`, so a fresh database and a patched one end up the same.
 *
 * Who is given these roles is a configuration step (Users & Roles), not part of this map.
 */
import type { RoleCode } from "./permissions";

export const FORM_ROLES: Readonly<Record<string, readonly RoleCode[]>> = {
  processing_drying: ["processing_supervisor", "plant_head", "admin"],
  processing_thermal: ["processing_supervisor", "plant_head", "admin"],
  // Priming is the Processing Supervisor's too, like Thermal (user, 2026-10-28).
  processing_priming: ["processing_supervisor", "plant_head", "admin"],
  // The colour sorter is the Processing Supervisor's, like Drying (user, 2026-10-28).
  processing_color_sort: ["processing_supervisor", "plant_head", "admin"],
  // And the Treatment Slip, treatment done after processing (QC review, 2026-11-03).
  processing_treatment: ["processing_supervisor", "plant_head", "admin"],
  processing_slip: ["processing_supervisor", "plant_head", "admin"],
  // gstack-shortcut(dec-05ef559e): today's combined clean-and-pack slip is admin-only (plus the plant
  // head's cancel), upgrade when the Packing module's own Packing Slip (key `packing_slip`) exists.
  processing_packing_slip: ["plant_head", "admin"],
  // Lot Blending is the Processing Supervisor's by default too (user, 2026-10-03).
  processing_blending: ["processing_supervisor", "plant_head", "admin"],
  // The packing work order is the Packing Supervisor's (docs/designs/packaging-module.md P2).
  packing_work_order: ["packing_supervisor", "plant_head", "admin"],
  // And its Re-Printing twin (user, 2026-10-28).
  reprint_work_order: ["packing_supervisor", "plant_head", "admin"],
  // And the Root Stock Nos Count: packed root stock with no seed count yet, counted once.
  root_stock_nos_count: ["packing_supervisor", "plant_head", "admin"],
  // Pack Seed Unpacking (P2C) is the packing floor's too (user, 2026-10-28).
  pack_p2c: ["packing_supervisor", "plant_head", "admin"],
  // And Inter Variety Transfer.
  ivt: ["packing_supervisor", "plant_head", "admin"],
  // And the OFL Packing Slip.
  ofl_packing_slip: ["packing_supervisor", "plant_head", "admin"],
  // And Pack Seed Re-Printing (with its QC sample request).
  pack_revalidate: ["packing_supervisor", "plant_head", "admin"],
  // And Unpackaging (master packs back to pouches).
  pack_unpacking: ["packing_supervisor", "plant_head", "admin"],
  // And the Packing Slip (Field Crop).
  fc_packing_slip: ["packing_supervisor", "plant_head", "admin"],
  // So is the Packing Slip that packs it (P3).
  packing_slip: ["packing_supervisor", "plant_head", "admin"],
};

export interface SeededFormGrants {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canSubmit: boolean;
  canCancel: boolean;
}

/** The row a system role gets on a form, or null for none (a role that sees no documents). */
export function seededFormGrants(
  role: RoleCode,
  permissions: readonly string[],
  formCode: string
): SeededFormGrants | null {
  const has = (p: string) => permissions.includes(p);
  const own: SeededFormGrants = {
    canView: has("document.view"),
    canCreate: has("document.create"),
    canEdit: has("document.edit"),
    canSubmit: has("document.submit"),
    canCancel: has("document.cancel"),
  };
  if (!Object.values(own).some(Boolean)) return null;
  const owners = FORM_ROLES[formCode];
  if (!owners || owners.includes(role)) return own;
  return {
    canView: own.canView,
    canCreate: false,
    canEdit: false,
    canSubmit: false,
    canCancel: false,
  };
}
